import { Injectable } from '@angular/core';
import {
  AppliedFilter,
  BaseFilter,
  CheckboxFilter,
  DropdownFilter,
  FilterCriteria,
  FilterOption,
  FilterType,
  MultiselectFilter,
} from '@shared/components/filters/Domain/filter.model';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  constructor(private translate: TranslateService) {}

  private activeFiltersSubject = new BehaviorSubject<AppliedFilter[]>([]);
  private composedValue: any[] = [];
  defaultSelectedFilters: AppliedFilter<BaseFilter>[] = [];

  setFilters(filters: AppliedFilter[]): void {
    this.activeFiltersSubject.next(filters);
  }

  resetFilters(): void {
    this.activeFiltersSubject.next([]);
  }

  get composedValues() {
    return this.composedValue;
  }

  get activeFiltersValue() {
    return this.activeFiltersSubject.getValue();
  }

  getDefaultFilters(): AppliedFilter<BaseFilter>[] {
    return this.defaultSelectedFilters;
  }

  get activeFiltersMap(): Record<string, AppliedFilter<BaseFilter>> {
    return this.activeFiltersSubject.getValue().reduce(
      (acc, f) => {
        acc[f.filter.id] = f;
        return acc;
      },
      {} as Record<string, AppliedFilter<BaseFilter>>,
    );
  }

  setDefaultFilters(filters: AppliedFilter<BaseFilter>[]): void {
    this.defaultSelectedFilters = [];
    this.defaultSelectedFilters = filters;
  }

  resetDefaultFilters(): void {
    this.defaultSelectedFilters = [];
  }

  removeFilter(filterId: string): void {
    if (filterId === 'composedFilter') {
      this.composedValue = [];
    }

    const currentFilters = this.activeFiltersSubject
      .getValue()
      .filter((f) => f.filter.id !== filterId);
    this.activeFiltersSubject.next(currentFilters);
  }

  clearFilters(): void {
    this.activeFiltersSubject.next([]);
    this.composedValue = [];
  }

  // Check if filter has options (Dropdown, Multiselect, Checkbox)
  isFilterWithOptions(
    filter: BaseFilter,
  ): filter is DropdownFilter | MultiselectFilter | CheckboxFilter {
    return (
      filter.type === FilterType.Dropdown ||
      filter.type === FilterType.Multiselect ||
      filter.type === FilterType.TreeSelect ||
      filter.type === FilterType.Checkbox
    );
  }

  // Get option labels for a filter
  getOptionLabels(appliedFilter: AppliedFilter): string {
    const { filter } = appliedFilter;

    if (!this.isFilterWithOptions(filter) || !filter.options) return '';

    if (!filter.options) return '';

    if (Array.isArray(appliedFilter.value)) {
      // Handle multi-select
      const labels = appliedFilter.value.map((val) => {
        const option = filter.options!.find((opt) => opt.value === val);
        return option ? option.label : val;
      });

      return labels.length > 2
        ? `${labels.slice(0, 2).join(', ')} + ${labels.length - 2} ${this.translate.instant('DATA.COMMON.GENERIC.MORE')}`
        : labels.join(', ');
    } else {
      // Handle single dropdown
      const option = filter.options.find((opt) => opt.value === appliedFilter.value);
      return option ? option.label : appliedFilter.value;
    }
  }

  getOptionLabelFromValue(
    appliedFilter: DropdownFilter | MultiselectFilter,
    value: unknown,
  ): { label: string; value: string } {
    if (!this.isFilterWithOptions(appliedFilter) || !appliedFilter.options)
      return { label: '', value: '' };

    if (!appliedFilter.options) return { label: '', value: '' };

    // Handle multi-select
    const foundFilter = appliedFilter.options!.find((opt) => opt.value === value);

    if (foundFilter) {
      return { label: foundFilter.label, value: foundFilter?.value };
    } else {
      return { label: '', value: '' };
    }
  }

  getOptionCount(filter: BaseFilter, value: any): number | undefined {
    if (!this.isFilterWithOptions(filter) || !filter.options) return 0;

    if (!filter.options) return undefined;
    const option = filter.options.find((opt) => opt.value === value) as FilterOption;
    return option?.count;
  }

  // Get filtered items for display
  getFilteredItems(value: any, maxItems: number = 2): { displayed: string[]; moreCount: number } {
    if (!value) return { displayed: [], moreCount: 0 };

    const items = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(',').map((item) => item.trim())
        : [];

    return {
      displayed: items.slice(0, maxItems),
      moreCount: items.length > maxItems ? items.length - maxItems : 0,
    };
  }

  getRadioButtonLabel(appliedFilter: AppliedFilter, maxItems: number = 2) {
    const { filter, value } = appliedFilter;

    if (!this.isFilterWithOptions(filter) || !filter.options || !value) {
      return { displayed: [], moreCount: 0 };
    }

    const items = Array.isArray(value) ? value : [value];

    const labels = items.map((val) => {
      const option = filter.options?.find((opt) => opt.value === val);
      return option ? option.label : val;
    });

    return {
      displayed: labels.slice(0, maxItems),
      moreCount: labels.length > maxItems ? labels.length - maxItems : 0,
    };
  }

  // Apply filter
  applyFilter(filterId: string, value: any, filters: Record<string, AppliedFilter>) {
    const filter = filters[filterId];
    if (!filter) return;

    const filterWithOptions = filter.filter;

    if (!this.isFilterWithOptions(filterWithOptions)) {
      const appliedFilter: AppliedFilter = {
        ...filter,
        isApplied: true,
        value: value,
      };

      return appliedFilter;
    }

    const validValue = Array.isArray(value)
      ? value.filter((v) => filterWithOptions.options.some((option) => option.value === v))
      : filterWithOptions.options.some((option) => option.value === value)
        ? value
        : null;

    const appliedFilter: AppliedFilter = {
      ...filter,
      isApplied: !!validValue,
      value: validValue,
    };

    return appliedFilter;
  }

  // Reset all filters
  resetAllFilters(filters: Record<string, AppliedFilter>) {
    Object.values(filters).forEach((filter) => {
      filter.isApplied = false;
      filter.value = null;
    });
  }

  isDefaultFiltersValid(
    defaultFilters: FilterCriteria,
    availableFilters: { id: string; [key: string]: any }[],
  ): boolean {
    const availableIds = new Set(availableFilters.map((f) => f.id));

    return Object.keys(defaultFilters).every((id) => availableIds.has(id));
  }

  areAppliedFiltersValid(
    appliedFilters: { filter: { id: string } }[],
    availableFilters: { id: string }[],
  ): boolean {
    if (appliedFilters.length === 0) {
      return false;
    }
    const availableIds = new Set(availableFilters.map((f) => f.id));
    return appliedFilters.every((af) => availableIds.has(af.filter.id));
  }

  sanitizeAppliedFilters(
    appliedFilters: { filter: { id: string }; isApplied: boolean; value: any }[],
    availableFilters: { id: string }[],
  ) {
    const validIds = new Set(availableFilters.map((f) => f.id));
    return appliedFilters.filter((af) => validIds.has(af.filter.id));
  }

  convertToAppliedFilters(
    defaultFilters: Record<string, any>,
    filters: BaseFilter[],
  ): AppliedFilter[] {
    return filters
      .map((filter) => {
        let value;

        // Normal filters, just pick by id
        value = defaultFilters[filter.id];
        if (typeof value === 'undefined') {
          return undefined;
        }

        return {
          filter,
          isApplied: true,
          value,
        };
      })
      .filter((appliedFilter): appliedFilter is AppliedFilter => appliedFilter !== undefined);
  }
}
